<script setup lang="ts">
import { useXPixel } from "~/composables/useXPixel";
import {
  getPurchaseEligibility,
  PURCHASE_UNAVAILABLE_REASON_LOW_VALUE,
} from "~/utils/purchase-eligibility.utils";
import { BaseButtonSubmit, NuxtIcon } from "#components";
const { loadXPixel, trackEventXPixel } = useXPixel();
onMounted(() => {
  loadXPixel();
});
const config = useRuntimeConfig();
const { marketplaceFeesPercent, minimumPrice } = config.public;
const xPixelTrackingEnabledForSetId = String(
  config.public?.xPixelTrackingEnabledForSetId
);
const GFALExStore = useGFALExStore();
function sendXPixelConversion_PurchaseStart(
  vValue: number | null,
  vCurrency: string | null
) {
  if (targetItem.value?.categoryId === xPixelTrackingEnabledForSetId) {
    trackEvent("tw-on8c3-q3e12");
    if (typeof window?.twq === "function") {
      trackEventXPixel("tw-on8c3-q3e12", {
        value: vValue,
        currency: vCurrency,
      });
    } else {
      trackEvent("tw-on8c3-q3e12-BLOCKED-BY-CLIENT");
      console.warn(
        "Twitter Pixel tw-on8c3-q3e12 not loaded or blocked by client."
      );
    }
  }
}

const props = defineProps({
  item: {
    type: Object as PropType<Item>,
    required: false,
    default: null,
  },
  collectible: {
    type: Object as PropType<Collectible>,
    required: false,
    default: null,
  },
  buyCheapestInSet: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const route = useRoute();
const { t } = useI18n();
const { trackEvent } = useGAnalytics();
const isModalBuyShown = ref<boolean>(false);
const purchased = toRef(props.item ? props.item.purchased : false);
const showPurchaseUnavailableTooltip = ref(false);
const timeRemaining = ref<number>(0);
const isTimeRemaining = computed(() => timeRemaining.value > 0);
const minutesLeft = computed(() => new Date(timeRemaining.value).getMinutes());
const secondsLeft = computed(() => new Date(timeRemaining.value).getSeconds());
let countdownInterval: ReturnType<typeof setInterval>;
const targetItem = computed<BaseMarketItem | null>(
  () => (props.item as BaseMarketItem) || (props.collectible as BaseMarketItem)
);
const buyUnavailableTooltipText = computed(() =>
  t("main.storeDetail.itemUnavailableBelowFees")
);

const feeCoverageEligibility = computed(() => {
  return getPurchaseEligibility(
    {
      priceGFAL: targetItem.value?.price?.GFAL,
      isPurchasable: targetItem.value?.isPurchasable,
      purchaseBlockReason: targetItem.value?.purchaseBlockReason,
    },
    {
      marketplaceFeesPercent: Number(marketplaceFeesPercent),
      minimumPriceUSD: Number(minimumPrice),
      gfalExRate: Number(GFALExStore.GFALExRate),
    }
  );
});
const shouldEnforceFeeCoverageRule = computed(
  () => Boolean(props.collectible) || route.path.includes("/market/")
);

const isPurchaseUnavailableByFeeCoverage = computed(() => {
  return (
    shouldEnforceFeeCoverageRule.value &&
    !feeCoverageEligibility.value.isPurchasable &&
    feeCoverageEligibility.value.purchaseBlockReason ===
      PURCHASE_UNAVAILABLE_REASON_LOW_VALUE
  );
});

const isItemBuyDisabled = computed(
  () => isTimeRemaining.value || isPurchaseUnavailableByFeeCoverage.value
);
const isCollectibleBuyDisabled = computed(
  () => !props.collectible?.price || isPurchaseUnavailableByFeeCoverage.value
);

const openBuyModal = () => {
  if (
    (props.item && isItemBuyDisabled.value) ||
    (props.collectible && isCollectibleBuyDisabled.value)
  ) {
    return;
  }

  isModalBuyShown.value = true;
  trackEvent(`buy_${route.name as string}`);
  sendXPixelConversion_PurchaseStart(Number(targetItem.value?.price?.GFAL), `GFAL`);
};

const setUnavailableTooltipVisibility = (isVisible: boolean) => {
  showPurchaseUnavailableTooltip.value = isVisible;
};

const buttonText = computed(() => {
  if (isTimeRemaining.value) {
    return (
      t("main.storeDetail.buy") +
      " (" +
      minutesLeft.value +
      t("main.storeDetail.minutes") +
      " " +
      secondsLeft.value +
      t("main.storeDetail.seconds") +
      ")"
    );
  }
  return t("main.storeDetail.buy");
});

const checkNextTime = () => {
  if (props.item && props.item.nextTime) {
    const now = new Date().getTime();
    const next = new Date(props.item.nextTime).getTime();
    timeRemaining.value = next - now;
    countdownInterval = setInterval(() => {
      timeRemaining.value -= 1000;

      if (timeRemaining.value <= 0) {
        clearInterval(countdownInterval);
        timeRemaining.value = 0;
      }
    }, 1000);
  }
};

onMounted(() => {
  checkNextTime();
});

onUnmounted(() => {
  if (countdownInterval) clearInterval(countdownInterval);
});

watch(
  () => route.query.checkout,
  (value) => {
    if (value) openBuyModal();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex items-center gap-4 flex-wrap">
    <div
      v-if="item && !purchased && item.number == 0"
      class="bg-primary-900 rounded-sm p-1.5 flex items-start gap-2 mr-2">
      <NuxtIcon
        name="mdi:information"
        class="text-rarity-exotic h-4 w-4 flex-shrink-0 mt-1" />
      <ClientOnly>
        <p class="text-rarity-exotic text-left">
          {{ $t("main.cardCollectible.soldVisitMarketBegin") }}
          <NuxtLinkLocale
            :to="`/market/${item.merchantId}/stack/${item.setId}`"
            class="underline">
            {{ $t("main.cardCollectible.soldVisitMarketLink") }}
          </NuxtLinkLocale>
          {{ $t("main.cardCollectible.soldVisitMarketEnd") }}
        </p>
      </ClientOnly>
    </div>
    <div
      v-else-if="item && !purchased"
      class="relative"
      @mouseenter="setUnavailableTooltipVisibility(true)"
      @mouseleave="setUnavailableTooltipVisibility(false)"
      @focusin="setUnavailableTooltipVisibility(true)"
      @focusout="setUnavailableTooltipVisibility(false)">
      <span :tabindex="isPurchaseUnavailableByFeeCoverage ? 0 : -1">
        <BaseButtonSubmit
          :disabled="isItemBuyDisabled"
          data-test="button-buy"
          class="w-72"
          @click="openBuyModal"
          :contextual-styles="true">
          <template #image-prefix>
            <BaseIcon
              v-if="isTimeRemaining"
              name="lock"
              class="w-4 h-4 text-neutral-300" />
          </template>
          <template #text>
            {{ buttonText }}
          </template>
          <template #image-suffix>
            <NuxtIcon
              v-if="!isTimeRemaining"
              name="mdi:cart"
              class="w-4 h-4 merchant-cta-icon--submit" />
          </template>
        </BaseButtonSubmit>
      </span>
      <CommonTooltip v-if="isPurchaseUnavailableByFeeCoverage" :is-visible="showPurchaseUnavailableTooltip">
        <template #tooltip-text>
          <p class="text-xs text-black font-semibold normal-case text-center max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap">
            {{ buyUnavailableTooltipText }}
          </p>
        </template>
      </CommonTooltip>
    </div>
    <div
      v-else-if="collectible"
      class="relative"
      @mouseenter="setUnavailableTooltipVisibility(true)"
      @mouseleave="setUnavailableTooltipVisibility(false)"
      @focusin="setUnavailableTooltipVisibility(true)"
      @focusout="setUnavailableTooltipVisibility(false)">
      <span :tabindex="isPurchaseUnavailableByFeeCoverage ? 0 : -1">
        <BaseButtonSubmit
          :disabled="isCollectibleBuyDisabled"
          data-test="button-buy"
          class="w-72"
          @click="openBuyModal"
          :contextual-styles="true">
          <template #text>
            {{
              buyCheapestInSet
                ? $t("main.storeDetail.buyCheapest")
                : $t("main.storeDetail.buy")
            }}
          </template>
          <template #image-suffix>
            <NuxtIcon
              name="mdi:cart"
              class="w-4 h-4 merchant-cta-icon--submit" />
          </template>
        </BaseButtonSubmit>
      </span>
      <CommonTooltip v-if="isPurchaseUnavailableByFeeCoverage" :is-visible="showPurchaseUnavailableTooltip">
        <template #tooltip-text>
          <p class="text-xs text-black font-semibold normal-case text-center max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap">
            {{ buyUnavailableTooltipText }}
          </p>
        </template>
      </CommonTooltip>
    </div>
    <div
      v-else-if="!collectible"
      class="bg-primary-900 p-2 flex items-center gap-2 rounded-sm">
      <NuxtIcon name="mdi:information" class="text-rarity-exotic" />
      <p class="text-rarity-exotic text-sm">
        {{ $t("main.storeDetail.purchased") }}
      </p>
    </div>
  </div>
  <ModalBuy
    v-if="isModalBuyShown"
    :item="item ? item : collectible"
    :is-limited-edition="item?.nextPrice ? true : false"
    @close="isModalBuyShown = false"
    @is-purchased="purchased = true"
    @update-collectible="$router.go(0)" />
</template>
